import { FunctionComponent, useEffect } from 'react';

// Types
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

// Components
import Page from '@Components/Utils/Page';
import Image from '@Components/Utils/Image';
import Anchor from '@Components/Utils/Anchor';
import PostCard from '../Components/PostCard';
import Share from '@Components/Utils/Anchor/Share';
import DownloadApp from '../Components/DownloadApp';
import LayoutContainer from '@Components/Layout/Components/Container';
import Breadcrumb, { PathLevelProps } from '@Components/UI/Breadcrumb';
import LayoutResult from '@Components/Layout/Search/Components/LayoutResult';

// Utils
import classnames from 'classnames';
import QueryString from 'query-string';

// Contexts
import { useViewDetected } from '@Components/Utils/ViewDetected';
import { usePostManager, withPostManager } from '../Contexts/post';

// APIs
import postApi from '@Libs/api/cms/posts';

// Styles
import styles from './PostDetail.module.scss';

// Type
import Post from './PostDetail';
import { NextSeoProps } from 'next-seo';

import { parse } from 'node-html-parser';
// Config
import { getServerSideHttp } from '@Libs/http/cms';

interface PostDetailProps {
    post: Post;
}

const paths = (post: Post): PathLevelProps[] => {
    const category = post.category;

    return [
        {
            label: 'Thư viện sức khoẻ',
            path: '/bai-viet',
        },
        category && {
            label: category?.title,
            path: `/bai-viet/danh-muc/${category?.slug}`,
        },
        {
            label: post?.title,
        },
    ];
};

const PostDetail: FunctionComponent<InferGetServerSidePropsType<typeof getServerSideProps> & PostDetailProps> = ({
    isPreviewing,
}) => {
    const { deviceType } = useViewDetected();
    const { post, category, relatedPosts } = usePostManager();

    const className = classnames([
        styles.wrapper,
        {
            [styles.mobile]: ['mobile', 'tablet'].includes(deviceType),
        },
    ]);

    useEffect(() => {
        const script = document.createElement('script');
        script.innerHTML = `
            lzli('lzi')();
        `;

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, [post.id]);

    const renderBannerRight = () => {
        return (
            <>
                {relatedPosts?.length > 0 && (
                    <PostCard className={styles.post_related} posts={relatedPosts.slice(0, 5)} />
                )}
                <DownloadApp deviceType={deviceType} />
            </>
        );
    };

    return (
        <Page className={className}>
            <LayoutContainer>
                <div className={styles.header}>
                    <div className={styles.left_content}>
                        <Breadcrumb
                            paths={paths(post)}
                            className={styles.breadcrumb}
                            itemClassName={styles.route_item}
                        />

                        <div className={styles.text}>
                            {category && (
                                <Anchor
                                    href={`/bai-viet/danh-muc/${category?.id}`}
                                    className={styles.post_category}
                                    style={{ background: category?.color }}
                                >
                                    {category?.title}
                                </Anchor>
                            )}
                            <h1 className={styles.post_title}>{post?.title}</h1>
                        </div>
                    </div>
                </div>
            </LayoutContainer>

            <div className={styles.body}>
                <LayoutResult
                    extendComponent={renderBannerRight()}
                    className={styles.layout_result}
                    bannerClassName={styles.sticky_banner}
                >
                    <div className={styles.post_info}>
                        <div className={styles.top}>
                            <Share key={`post_${post.id}_share`} className={styles.share}>
                                <Image className={styles.share_icon} src="/assets/bai-viet/share.png" />
                                <span>Chia sẻ</span>
                            </Share>
                        </div>

                        <Image
                            alt={post?.title}
                            className={styles.banner}
                            width={post?.image?.width}
                            height={post?.image?.height}
                            src={`${post?.image?.url}?width=${deviceType === 'desktop' ? 800 : 500}`}
                        />

                        {post?.description && (
                            <div
                                className={styles.description}
                                dangerouslySetInnerHTML={{
                                    __html: `${post?.description}`,
                                }}
                            />
                        )}

                        {post?.content && (
                            <div
                                className={styles.content}
                                dangerouslySetInnerHTML={{
                                    __html: `${post?.content}`,
                                }}
                            />
                        )}
                    </div>
                </LayoutResult>
            </div>

            {isPreviewing && <div className={styles.block_preview}>Bạn đang xem ở trạng thái preview</div>}
        </Page>
    );
};

export default withPostManager(PostDetail);

const processHTML = (inputHTML, post) => {
    if (!inputHTML) return '';

    const htmlDOM = parse(inputHTML, {
        blockTextElements: {
            script: false,
            noscript: false,
        },
    });

    const images = htmlDOM.querySelectorAll('img');
    (images || []).forEach((image: any) => {
        image.setAttribute('data-src', image.getAttribute('src'));
        image.setAttribute('src', '/assets/1.gif');

        // image.classList.add('lzi');
        image.setAttribute('class', `${image.getAttribute('class') || ''} lzi`);

        if (!image.getAttribute('alt')) {
            image.setAttribute('alt', post?.title);
        }
    });

    return htmlDOM.toString();
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const http = getServerSideHttp(ctx);
    const isPreviewing = !!ctx?.query?.preview_token;

    const postId: any = ctx?.params?.id;
    if (!postId) {
        return {
            notFound: true,
        };
    }
    // const post = await postApi(http).findOne(postId, {
    //     ...(isPreviewing && {
    //         preview_token: ctx?.query?.preview_token,
    //     }),
    // });

    const post = await (async () => {
        try {
            return await postApi(http).findOne(postId);
        } catch (error) {
            return {};
        }
    })();

    if (!post?.slug || !post?.id) {
        return {
            notFound: true,
        };
    }

    if (post.slug && post.slug !== postId) {
        const destination = `/bai-viet/${post.slug}${ctx.query ? `?${QueryString.stringify(ctx.query)}` : ''}`;

        return {
            redirect: {
                destination,
                statusCode: 301,
            },
        };
    }

    const metadata = post?.metadata;
    const openGraphDescription = metadata?.description || post.description;

    const title = `eDoctor: ${metadata?.title || post.title}`;
    const seo: NextSeoProps = {
        title,
        openGraph: {
            title,
            type: 'website',
            ...(openGraphDescription && { description: openGraphDescription.substr(0, 150) }),
            // ...(metadata?.image && {
            //     images: Object.values(metadata.image.formats).map((image: any) => ({
            //         alt: title,
            //         url: image?.url,
            //         width: image?.width,
            //         height: image?.height,
            //     })),
            // }),
        },
    };

    try {
        post.content = processHTML(post.content, post);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
    }

    return {
        props: {
            seo,
            post,
            isPreviewing,
        },
    };
};
